import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Printer, GripVertical, Eye } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", paper_size: "A4", orientation: "portrait", font_size: 10,
  header_company_name: true, header_establishment_name: true, header_branch: false,
  header_logo: true, header_report_title: true,
  footer_page_numbers: true, footer_generated_at: true,
  footer_custom_show: false, footer_custom_text: "",
  show_totals_row: true, status: "Active", columns: {},
};

const HEADER_OPTS = [
  ["header_company_name", "Company Name"], ["header_establishment_name", "Establishment Name"],
  ["header_branch", "Branch"], ["header_logo", "Logo"], ["header_report_title", "Report Title"],
];
const FOOTER_OPTS = [
  ["footer_page_numbers", "Page Numbers"], ["footer_generated_at", "Generated Date & Time"],
  ["footer_custom_show", "Custom Footer Text"],
];
const PAPER = { A4: [210, 297], A5: [148, 210], Letter: [216, 279] };

const REPORT_TYPES = [
  ["invoice", "Invoice"], ["jobcard", "Job Card"],
  ["sales", "Sales Report"], ["purchase", "Purchase Report"], ["stock", "Stock Report"],
];

const COLUMN_SETS = {
  invoice: [
    { key: "invoice_no", label: "Invoice No" }, { key: "date", label: "Date" },
    { key: "customer", label: "Customer" }, { key: "mobile", label: "Mobile" },
    { key: "vehicle", label: "Vehicle" }, { key: "items", label: "Items" },
    { key: "qty", label: "Qty" }, { key: "subtotal", label: "Subtotal" },
    { key: "discount", label: "Discount" }, { key: "tax", label: "Tax" },
    { key: "total", label: "Total" }, { key: "payment", label: "Payment Mode" },
    { key: "status", label: "Status" },
  ],
  jobcard: [
    { key: "job_no", label: "Job No" }, { key: "date", label: "Date" },
    { key: "customer", label: "Customer" }, { key: "vehicle", label: "Vehicle" },
    { key: "odometer", label: "Odometer" }, { key: "mechanic", label: "Mechanic" },
    { key: "complaints", label: "Complaints" }, { key: "services", label: "Services" },
    { key: "parts_total", label: "Parts Total" }, { key: "labour_total", label: "Labour Total" },
    { key: "grand_total", label: "Grand Total" }, { key: "status", label: "Status" },
  ],
  sales: [
    { key: "date", label: "Date" }, { key: "invoice_no", label: "Invoice No" },
    { key: "customer", label: "Customer" }, { key: "items", label: "Items" },
    { key: "qty", label: "Qty" }, { key: "subtotal", label: "Subtotal" },
    { key: "discount", label: "Discount" }, { key: "tax", label: "Tax" },
    { key: "total", label: "Total" }, { key: "payment", label: "Payment Mode" },
  ],
  purchase: [
    { key: "date", label: "Date" }, { key: "bill_no", label: "Bill No" },
    { key: "supplier", label: "Supplier" }, { key: "items", label: "Items" },
    { key: "qty", label: "Qty" }, { key: "subtotal", label: "Subtotal" },
    { key: "tax", label: "Tax" }, { key: "total", label: "Total" },
  ],
  stock: [
    { key: "part_no", label: "Part No" }, { key: "item_name", label: "Item Name" },
    { key: "company", label: "Company" }, { key: "category", label: "Category" },
    { key: "hsn", label: "HSN" }, { key: "stock", label: "Stock" },
    { key: "purchase_rate", label: "Purchase Rate" }, { key: "mrp", label: "MRP" },
    { key: "stock_value", label: "Stock Value" },
  ],
};

const SAMPLES = {
  invoice_no: "INV-1024", job_no: "JC-261002-AB12", date: "02 Oct 2026",
  customer: "Ram Kumar", mobile: "98765 43210", vehicle: "Splendor · TN01AB1234",
  items: "3", qty: "5", subtotal: "₹1,500.00", discount: "₹50.00", tax: "₹261.00",
  total: "₹1,711.00", payment: "Cash", status: "Paid", odometer: "12,345 KM",
  mechanic: "Amit", complaints: "Brake noise", services: "General Service",
  parts_total: "₹1,180.00", labour_total: "₹450.00", grand_total: "₹1,630.00",
  bill_no: "PB-88", supplier: "PartsHub Traders", part_no: "BP-101",
  item_name: "Brake Pads (Front)", company: "AMRON", category: "BRAKES",
  hsn: "8708", stock: "24", purchase_rate: "₹280.00", mrp: "₹450.00", stock_value: "₹6,720.00",
};

function ColumnsPreview({ open, onClose, f, selType }) {
  const all = COLUMN_SETS[selType];
  const keys = f.columns?.[selType] ?? all.map(c => c.key);
  const cols = keys.map(k => all.find(c => c.key === k)).filter(Boolean);
  const fs = Math.max(6, Number(f.font_size) || 10);
  const title = REPORT_TYPES.find(([v]) => v === selType)?.[1] || selType;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="pt-columns-preview">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Print Preview — {title} · {f.paper_size} · {f.orientation}</DialogTitle>
        </DialogHeader>
        {!cols.length && <div className="text-xs text-muted-foreground" data-testid="pt-preview-empty">No columns selected for this report type.</div>}
        {cols.length > 0 && (
          <div className="bg-white text-black rounded-sm overflow-x-auto" style={{ fontSize: fs * 0.9 }}>
            {(f.header_company_name || f.header_report_title) && (
              <div className="border-b-2 border-neutral-800 p-3 text-center">
                {f.header_company_name && <div className="font-bold" style={{ fontSize: fs * 1.3 }}>HAMAR CARE PHARMACY</div>}
                {f.header_report_title && <div className="uppercase tracking-widest font-semibold mt-1" style={{ fontSize: fs }}>{title}</div>}
              </div>
            )}
            <table className="w-full" data-testid="pt-preview-table" style={{ fontSize: fs * 0.9 }}>
              <thead>
                <tr className="border-b border-neutral-400">
                  {cols.map(c => <th key={c.key} className="text-left p-1.5 font-bold whitespace-nowrap" data-testid={`pt-preview-th-${c.key}`}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {[0, 1, 2].map(r => (
                  <tr key={r} className="border-b border-neutral-200">
                    {cols.map(c => <td key={c.key} className="p-1.5 whitespace-nowrap">{SAMPLES[c.key] || "—"}</td>)}
                  </tr>
                ))}
                {f.show_totals_row && (
                  <tr className="border-t-2 border-neutral-800 font-bold" data-testid="pt-preview-totals-row">
                    {cols.map((c, i) => (
                      <td key={c.key} className="p-1.5">{i === 0 ? "Total" : ["total", "grand_total", "stock_value", "subtotal"].includes(c.key) ? SAMPLES[c.key] : ""}</td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
            {(f.footer_page_numbers || f.footer_generated_at || (f.footer_custom_show && f.footer_custom_text)) && (
              <div className="border-t border-neutral-300 p-2 text-neutral-500 flex justify-between gap-2" style={{ fontSize: fs * 0.75 }}>
                <span className="truncate">{f.footer_custom_show && f.footer_custom_text ? f.footer_custom_text : ""}</span>
                <span className="whitespace-nowrap">{f.footer_generated_at ? `Generated: ${new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</span>
                <span className="whitespace-nowrap">{f.footer_page_numbers ? "Page 1 of 1" : ""}</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ColumnSelector({ f, setF }) {
  const [selType, setSelType] = useState("invoice");
  const [dragKey, setDragKey] = useState(null);
  const [preview, setPreview] = useState(false);
  const all = COLUMN_SETS[selType];
  const selected = f.columns?.[selType] ?? all.map(c => c.key);
  const unselected = all.filter(c => !selected.includes(c.key));
  const write = (keys) => setF(x => ({ ...x, columns: { ...x.columns, [selType]: keys } }));
  const toggleCol = (key, on) => write(on ? [...selected, key] : selected.filter(k => k !== key));
  const dropOn = (target) => {
    if (!dragKey || dragKey === target) { setDragKey(null); return; }
    const keys = selected.filter(k => k !== dragKey);
    keys.splice(keys.indexOf(target), 0, dragKey);
    write(keys);
    setDragKey(null);
  };
  return (
    <div data-testid="pt-column-selector">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Column Selector</div>
        <Button type="button" size="sm" variant="outline" data-testid="pt-preview-btn" onClick={() => setPreview(true)}
          className="rounded-sm uppercase tracking-[0.15em] text-[10px] h-7">
          <Eye className="w-3 h-3 mr-1"/> Preview
        </Button>
      </div>
      <div className="mb-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Report / Transaction Type</Label>
        <Select value={selType} onValueChange={setSelType}>
          <SelectTrigger data-testid="pt-col-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
          <SelectContent>
            {REPORT_TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`pt-col-type-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="border border-border rounded-sm divide-y divide-border" data-testid="pt-col-list">
        {selected.map(key => {
          const c = all.find(x => x.key === key);
          if (!c) return null;
          return (
            <div key={key} draggable data-testid={`pt-col-${key}`}
              onDragStart={() => setDragKey(key)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropOn(key)}
              className={`flex items-center gap-2 px-2.5 py-1.5 bg-secondary/40 ${dragKey === key ? "opacity-50" : ""}`}>
              <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab shrink-0" data-testid={`pt-col-drag-${key}`}/>
              <Checkbox data-testid={`pt-col-check-${key}`} checked onCheckedChange={(v) => toggleCol(key, !!v)}/>
              <span className="text-xs flex-1">{c.label}</span>
            </div>
          );
        })}
        {unselected.map(c => (
          <div key={c.key} data-testid={`pt-col-${c.key}`} className="flex items-center gap-2 px-2.5 py-1.5 opacity-60">
            <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0"/>
            <Checkbox data-testid={`pt-col-check-${c.key}`} checked={false} onCheckedChange={(v) => toggleCol(c.key, !!v)}/>
            <span className="text-xs flex-1 text-muted-foreground">{c.label}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5" data-testid="pt-col-count">
        {selected.length} of {all.length} columns selected · drag to reorder
      </div>
      <ColumnsPreview open={preview} onClose={() => setPreview(false)} f={f} selType={selType}/>
    </div>
  );
}

function CheckRow({ k, label, f, setF }) {
  return (
    <label className="flex items-center justify-between border border-border rounded-sm p-2.5 cursor-pointer hover:bg-secondary/40" data-testid={`pt-row-${k}`}>
      <span className="text-xs">{label}</span>
      <Checkbox data-testid={`pt-${k}`} checked={!!f[k]} onCheckedChange={(v) => setF(x => ({ ...x, [k]: !!v }))}/>
    </label>
  );
}

function TemplatePreview({ f }) {
  const [pw, ph] = PAPER[f.paper_size] || PAPER.A4;
  const land = f.orientation === "landscape";
  const ratio = land ? `${ph} / ${pw}` : `${pw} / ${ph}`;
  const fs = Math.max(6, Number(f.font_size) || 10);
  return (
    <div className="border border-border bg-secondary/30 rounded-sm p-4" data-testid="pt-preview">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
        <Printer className="w-3 h-3"/> Layout Preview · {f.paper_size} · {f.orientation}
      </div>
      <div className="mx-auto bg-white text-black rounded-sm shadow-lg flex flex-col overflow-hidden"
        style={{ aspectRatio: ratio, maxHeight: 420, fontSize: fs * 0.9 }} data-testid="pt-preview-paper">
        {(f.header_company_name || f.header_establishment_name || f.header_branch || f.header_logo || f.header_report_title) && (
          <div className="border-b-2 border-neutral-800 p-3" data-testid="pt-preview-header">
            <div className="flex items-center gap-2">
              {f.header_logo && <div className="w-8 h-8 bg-neutral-300 rounded-sm shrink-0 flex items-center justify-center text-[8px] text-neutral-500">LOGO</div>}
              <div className="min-w-0">
                {f.header_company_name && <div className="font-bold truncate" style={{ fontSize: fs * 1.3 }}>HAMAR CARE PHARMACY</div>}
                {f.header_establishment_name && <div className="text-neutral-600 truncate" style={{ fontSize: fs * 0.85 }}>Main Workshop Establishment</div>}
                {f.header_branch && <div className="text-neutral-500 truncate" style={{ fontSize: fs * 0.75 }}>Branch: City Centre</div>}
              </div>
            </div>
            {f.header_report_title && <div className="mt-2 text-center font-bold uppercase tracking-widest" style={{ fontSize: fs }}>Tax Invoice</div>}
          </div>
        )}
        <div className="flex-1 p-3 space-y-1.5" data-testid="pt-preview-body" style={{ fontSize: fs * 0.9 }}>
          {[["Brake Pads (Front)", "2 × ₹450.00", "₹900.00"], ["Engine Oil 10W-30", "1 × ₹520.00", "₹520.00"], ["Labour — General Service", "", "₹330.00"]].map(([n, q, a]) => (
            <div key={n} className="flex justify-between border-b border-neutral-200 pb-1">
              <span className="truncate">{n}</span><span className="text-neutral-500 mx-2 whitespace-nowrap">{q}</span><span className="font-mono whitespace-nowrap">{a}</span>
            </div>
          ))}
          {f.show_totals_row && (
            <div className="flex justify-between font-bold border-t-2 border-neutral-800 pt-1 mt-1" data-testid="pt-preview-totals" style={{ fontSize: fs }}>
              <span>Total</span><span className="font-mono">₹1,750.00</span>
            </div>
          )}
        </div>
        {(f.footer_page_numbers || f.footer_generated_at || (f.footer_custom_show && f.footer_custom_text)) && (
          <div className="border-t border-neutral-300 p-2 text-neutral-500 flex items-center justify-between gap-2" style={{ fontSize: fs * 0.75 }} data-testid="pt-preview-footer">
            <span className="truncate">{f.footer_custom_show && f.footer_custom_text ? f.footer_custom_text : ""}</span>
            <span className="whitespace-nowrap">{f.footer_generated_at ? `Generated: ${new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</span>
            <span className="whitespace-nowrap">{f.footer_page_numbers ? "Page 1 of 1" : ""}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PrintTemplateForm() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!tid);

  useEffect(() => {
    if (!tid) return;
    api.get(`/print-templates/${tid}`)
      .then(r => { setF({ ...empty, ...r.data }); setLoaded(true); })
      .catch(e => { toast.error(formatErr(e)); navigate("/settings/print"); });
  }, [tid]);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Template Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, font_size: Number(f.font_size) || 10, footer_custom_text: f.footer_custom_text || null };
      if (tid) await api.patch(`/print-templates/${tid}`, payload);
      else await api.post("/print-templates", payload);
      toast.success(tid ? "Template updated" : "Template created");
      navigate("/settings/print");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8" data-testid="print-template-form-page">
      <div className="flex items-center gap-3 mb-6">
        <Button type="button" data-testid="pt-back-btn" variant="outline" onClick={() => navigate("/settings/print")} className="rounded-sm text-xs uppercase tracking-wider">
          <ArrowLeft className="w-3 h-3 mr-1"/> Back to Print Settings
        </Button>
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-1">Settings · Print & PDF</div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tighter">{tid ? "Edit" : "New"} Print Template</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="border border-border bg-card rounded-sm p-5 space-y-4" data-testid="pt-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Template Name *</Label>
            <Input data-testid="pt-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} placeholder="e.g. Compact Invoice A5" className="mt-1 rounded-sm bg-secondary border-border"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paper Size</Label>
              <Select value={f.paper_size} onValueChange={(v) => setF(x => ({ ...x, paper_size: v }))}>
                <SelectTrigger data-testid="pt-paper-size" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A4" data-testid="pt-paper-a4">A4</SelectItem>
                  <SelectItem value="A5" data-testid="pt-paper-a5">A5</SelectItem>
                  <SelectItem value="Letter" data-testid="pt-paper-letter">Letter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Orientation</Label>
              <Select value={f.orientation} onValueChange={(v) => setF(x => ({ ...x, orientation: v }))}>
                <SelectTrigger data-testid="pt-orientation" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait" data-testid="pt-orient-portrait">Portrait</SelectItem>
                  <SelectItem value="landscape" data-testid="pt-orient-landscape">Landscape</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Font Size (pt)</Label>
              <Input data-testid="pt-font-size" type="number" min="6" max="24" value={f.font_size} onChange={(e) => setF(x => ({ ...x, font_size: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
              <Select value={f.status} onValueChange={(v) => setF(x => ({ ...x, status: v }))}>
                <SelectTrigger data-testid="pt-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active" data-testid="pt-status-active">Active</SelectItem>
                  <SelectItem value="Inactive" data-testid="pt-status-inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Header Options</div>
            <div className="grid grid-cols-2 gap-2">
              {HEADER_OPTS.map(([k, l]) => <CheckRow key={k} k={k} label={l} f={f} setF={setF}/>)}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Footer Options</div>
            <div className="grid grid-cols-2 gap-2">
              {FOOTER_OPTS.map(([k, l]) => <CheckRow key={k} k={k} label={l} f={f} setF={setF}/>)}
            </div>
            {f.footer_custom_show && (
              <Input data-testid="pt-footer-custom-text" value={f.footer_custom_text || ""} onChange={(e) => setF(x => ({ ...x, footer_custom_text: e.target.value }))}
                placeholder="Custom footer text (e.g. Thank you! Visit again)" className="mt-2 rounded-sm bg-secondary border-border"/>
            )}
          </div>

          <ColumnSelector f={f} setF={setF}/>

          <CheckRow k="show_totals_row" label="Show Totals Row" f={f} setF={setF}/>

          <div className="flex gap-2 pt-2">
            <Button data-testid="pt-save" onClick={save} disabled={busy || !f.name.trim()} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              {busy ? "Saving…" : tid ? "Update Template" : "Save Template"}
            </Button>
            <Button data-testid="pt-cancel" variant="outline" onClick={() => navigate("/settings/print")} className="rounded-sm uppercase tracking-[0.15em]">Cancel</Button>
          </div>
        </div>

        <TemplatePreview f={f}/>
      </div>
    </div>
  );
}
